/**
 * Mini cart component with B2B pricing support
 * Displays cart items, quantities, pricing, discounts, promolines, and free shipping progress
 */

import clsx from 'clsx'

import { useEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { useGwp } from '@/components/hooks/useGwp'
import { GwpCartBlock, GwpGiftItem } from '@/components/custom-elements/gwp-cart-block'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p }) => {
  return (
    <div className={`group relative shrink-0 cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        className=" absolute z-2 size-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 inset-tl"
        aria-label={value}
      />
      <div className={className}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" />
  const minusIcon = <Icon id="minus" />
  const trashIcon = <span>Remove</span>
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)
      /** Throw an error is the new quantity is over the available stock */
      handleLineItemQuantityChange(item, newQuantity)
      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <div className="flex flex-col gap-y-0.5">
      <form onChange={onLineItemQuantityChange} className="flex items-center justify-between gap-x-0.5 lg:gap-x-1">
        <div className="flex shrink-0 rounded-8 border border-neutral-medium">
          <OnChangeTrigger
            p="btn btn-primary btn-transparent btn-square w-[34px] !border-0"
            type={actionKeys.MINUS}
            value={minusIcon}
            className={'size-fit children:h-[2px] children:w-[14px]'}
          />
          <div className="paragraph-s flex w-2 grow items-center justify-center text-center font-medium !leading-none">
            {item.quantity}
          </div>
          <OnChangeTrigger
            p="btn btn-primary btn-transparent btn-square w-[34px] !border-0"
            type={actionKeys.PLUS}
            value={plusIcon}
            className={'size-fit children:size-[14px]'}
          />
        </div>

        <OnChangeTrigger
          p="paragraph-s font-medium text-neutral-secondary text-line text-line-space link-fade"
          type={actionKeys.REMOVE}
          value={trashIcon}
          className={''}
        />
      </form>
      <p className={clsx('paragraph-xs hidden w-full text-system-error transition-all', error && '!flex')}>{error}</p>
    </div>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message =
    reachedGoal <= 0 ? (
      <>
        <div className="flex h-[9px] w-[12px] shrink-0 children:size-full">
          <Icon id={'tick'} />
        </div>
        <p className="paragraph-s font-medium">You're receiving free shipping</p>
      </>
    ) : (
      <>
        <div className="flex h-[13px] w-[14px] shrink-0 children:size-full">
          <Icon id={'truck'} />
        </div>
        <p className="paragraph-s">
          You're <span className="font-medium">{formatMoney(reachedGoal)} away</span> from free shipping
        </p>
      </>
    )
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" className="flex flex-col">
      <div className="relative h-[4px] w-full bg-neutral-medium">
        <div
          className={clsx(
            'absolute h-full w-shipping-bar-width transition-all inset-tl',
            reachedGoal <= 0 ? 'bg-system-success' : 'bg-system-warning'
          )}
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
      <div
        className={clsx(
          'flex items-center gap-x-0.375 px-margin py-0.75 transition-all lg:px-1.5',
          reachedGoal <= 0 ? 'bg-system-success-light' : 'bg-system-warning-light'
        )}
      >
        {message}
      </div>
    </div>
  )
}

/**
 * B2B Line Price Component
 * Renders a b2b-price element directly in JSX
 */
const B2BLinePrice = ({ item }) => {
  return (
    <b2b-price
      className="group/price flex flex-col items-end text-right"
      data-variant-id={item.variant_id}
      data-quantity={item.quantity}
    >
      <span data-b2b-price className="paragraph-b group-data-[sale=true]/price:text-brand-sale"></span>
      <span
        data-original-price
        className="paragraph-b hidden line-through group-data-[sale=true]/price:block group-data-[sale=true]/price:text-neutral-secondary"
      ></span>
    </b2b-price>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div className="flex gap-x-0.25 lg:absolute lg:flex-col lg:items-end lg:text-right lg:inset-tr">
        <span className={`paragraph-b ${salePrice === 0 ? 'text-system-success' : 'text-brand-sale'}`}>
          {salePrice === 0 ? 'Free' : formatMoney(salePrice)}
        </span>
        <span className="paragraph-b text-neutral-secondary line-through">{formatMoney(originalPrice)}</span>
      </div>
    )
  }

  // For B2B customers, calculate line total from unit price
  const isB2BCustomer = window.globals?.customer?.b2b

  if (isB2BCustomer && item.variant_id) {
    return <B2BLinePrice item={item} />
  }

  // Priority order for non-B2B: Discounts -> Compare At -> Regular
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div className="flex gap-x-0.25 lg:absolute lg:flex-col lg:items-end lg:text-right lg:inset-tr">
      <span className={`paragraph-b ${item.original_line_price === 0 ? 'text-system-success' : ''}`}>
        {item.original_line_price === 0 ? 'Free' : formatMoney(item.original_line_price)}
      </span>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts && item.discounts.length > 0 && (
        <ul className="list-none">
          {item.discounts.map((discount) => {
            return (
              <li className="paragraph-xs flex list-none items-center gap-x-0.25 text-neutral-secondary">
                <div className="size-[14px] children:size-full">
                  <Icon id={'tag'} />
                </div>
                {discount.title}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} className="shrink-0 link-fade" aria-label="Item image">
      <PreactImage
        className="flex aspect-3/4 w-[110px] children:size-full children:object-cover"
        key={id}
        imageUrl={[imageSrc]}
      />
    </a>
  )
}

/**
 * Checks if item has universal sizing by looking for "Size" property
 * @param {Object} properties - Item properties object
 * @returns {boolean} - True if universal sizing exists
 */
const hasUniversalSizing = (properties) => {
  return properties && Object.prototype.hasOwnProperty.call(properties, 'Size')
}

/**
 * Formats size value based on gender and size array
 * @param {string} optionValue - The option value string
 * @param {string} gender - Product gender (Womens/Mens)
 * @returns {string} - Formatted size string or original value
 */
const formatSizeValue = (optionValue, gender) => {
  const itemSizesArray = optionValue.split(',')

  if (itemSizesArray.length <= 1) {
    return optionValue
  }

  let ukSizeValue = ''
  let usSizeValue = ''

  if (gender === 'Womens') {
    ukSizeValue = itemSizesArray[4] ? itemSizesArray[4].replace('WOMENS', '') : ''
    usSizeValue = itemSizesArray[5] ? itemSizesArray[5].replace('WOMENS', '') : ''
  } else if (gender === 'Mens') {
    ukSizeValue = itemSizesArray[6] ? itemSizesArray[6].replace('MENS', '') : ''
    usSizeValue = itemSizesArray[7] ? itemSizesArray[7].replace('MENS', '') : ''
  }

  const euSizeValue = itemSizesArray[3] ? itemSizesArray[3].replace('EURO', 'EU') : ''

  return `${usSizeValue} / ${ukSizeValue} / ${euSizeValue}`.trim()
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item, firstPromoline, secondPromoline }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0
  const productGender = item.product?.metafields?.utopia?.gender

  // Filter options to exclude size when universal sizing exists
  const filteredOptions = item.options_with_values.filter((option) => {
    if (option.name === 'Size' && hasUniversalSizing(properties)) {
      return false
    }
    return true
  })

  return (
    <div className="flex flex-row justify-between gap-x-0.75 lg:gap-x-1">
      <div className="relative flex grow flex-col lg:pr-6.25">
        <h3 className="paragraph-b font-medium">{item.customer_facing_product_name || item.product_title}</h3>
        <ItemPrice item={item} />
        <div className="mt-0.375 flex flex-col gap-y-0.375">
          {(item.colour_name || filteredOptions.length > 0 || hasProperties) && (
            <span className="paragraph-s text-neutral-secondary">
              {[
                item.colour_name,
                ...filteredOptions.map((option) =>
                  option.name === 'Size' ? formatSizeValue(option.value, productGender) : option.value
                ),
                ...(hasProperties && properties
                  ? Object.keys(properties)
                    .filter((property) => properties[property] && !property.startsWith('_'))
                    .map((property) => properties[property])
                  : [])
              ]
                .filter(Boolean)
                .map((val, idx) => (
                  <>
                    {idx > 0 && <span className="paragraph-b text-neutral-medium">&nbsp;|&nbsp;</span>}
                    {val}
                  </>
                ))}
            </span>
          )}
          <MiniCartItemDiscount item={item} />
        </div>
        {(firstPromoline || secondPromoline) && (
          <div className="mt-0.375 hidden flex-col gap-0.25 md:flex">
            {firstPromoline && (
              <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="small" />
            )}
            {secondPromoline && (
              <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="small" />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const MiniCartLineItem = ({ item, gift, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'

  const criteria = useMemo(
    () => ({
      tags: item.tags ?? [],
      collections: item.collections?.map((c) => c.handle) ?? [],
      products: [item.handle]
    }),
    [item.tags, item.collections, item.handle]
  )

  const allPromolines = usePromolines(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find((item) => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div
      className={`flex flex-col gap-y-0.625 border-b border-neutral-medium pb-1.5 transition-all last:border-b-0 last:pb-0 ${loadingState}`}
    >
      <div className="flex items-stretch gap-x-0.75 lg:gap-x-1">
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
        <div className="flex w-full grow flex-col justify-between gap-y-1">
          <MiniCartItemDetails item={item} firstPromoline={firstPromoline} secondPromoline={secondPromoline} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
        </div>
      </div>
      {(firstPromoline || secondPromoline) && (
        <div className="flex flex-col gap-0.25 md:hidden">
          {firstPromoline && (
            <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="medium" />
          )}
          {secondPromoline && (
            <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="medium" />
          )}
        </div>
      )}
      {gift && (
        <GwpGiftItem
          gift={gift}
          onAddToCart={({ loading, cart: newCart }) => {
            if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
            if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
          }}
          isLoading={isLoading}
          blockSpacing={true}
        />
      )}
    </div>
  )
}

/**
 * Calculates total savings from compare_at_price discounts
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const linePricePerItem = item.line_price / item.quantity
    const compareAtPrice = item.compare_at_price

    if (compareAtPrice && compareAtPrice > linePricePerItem) {
      const savingsPerItem = compareAtPrice - linePricePerItem
      const itemSavings = savingsPerItem * item.quantity
      compareAtSavings += itemSavings
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({
  total,
  totalCartDiscount,
  isLoading,
  cart,
  discountText,
  totalText,
  minicartRewardsText
}) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)
  const subtotal = formatMoney(total + totalDiscountAmount)
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div className="flex flex-col gap-y-0.5">
      {window?.globals?.cart?.minicartType === 'sidecart' ? (
        <div className="flex justify-between gap-x-1">
          <p className="paragraph-b font-medium">{totalText || 'Estimated Total'}</p>
          <p className={`paragraph-b font-medium transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
        </div>
      ) : (
        <div className="mb-0.5 flex flex-col gap-y-0.5">
          <div className="flex flex-col gap-y-0.25">
            <div className="flex justify-between gap-x-1">
              <p className="paragraph-s">
                {totalItems} {totalItems === 1 ? 'Item' : 'Items'}
              </p>
              <p className={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>{subtotal}</p>
            </div>
            {totalDiscountAmount > 0 && (
              <div className="flex justify-between gap-x-1">
                <p className="paragraph-s">{discountText || 'Savings'}</p>
                <p className={`text-system-sale paragraph-s transition-all ${isLoading && 'opacity-50'}`}>
                  -{totalDiscount}
                </p>
              </div>
            )}
            <div className="flex justify-between gap-x-1">
              <p className="paragraph-b font-medium">{totalText || 'Estimated Total'}</p>
              <p className={`paragraph-b font-medium transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
            </div>
          </div>
          <loyalty-cart-banner data-size="small" />
          {minicartRewardsText && (
            <div
              className="richtext paragraph-s mt-0.5 text-neutral-secondary"
              dangerouslySetInnerHTML={{ __html: minicartRewardsText }}
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading }) => {
  const buttons = window.minicart?.buttons || ''
  return (
    <div
      className={`flex h-fit w-full flex-wrap items-center justify-center gap-0.5 gap-x-1 self-center px-1.5 py-2 lg:py-1.5 md-max:flex-col ${
        isLoading ? 'opacity-0' : ''
      }`}
      dangerouslySetInnerHTML={{ __html: buttons }}
    ></div>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ minicartType, checkoutButtonTitle, itemCount = 0 }) => {
  return (
    <div>
      {minicartType === 'sidecart' ? (
        <a href="/cart" className="w-full btn btn-fill btn-primary btn-l" aria-label={checkoutButtonTitle}>
          View Your Bag {itemCount > 0 && ` (${itemCount})`}
        </a>
      ) : (
        <a href="/checkout" className="w-full btn btn-fill btn-primary btn-l" aria-label={checkoutButtonTitle}>
          {checkoutButtonTitle}
        </a>
      )}
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 * When minicartType is sidecart, splits into "Added to Bag" (most recent item) and "Other Items in your bag (N)".
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ minicartType, cart, giftsWithoutThreshold, handleSetCartConfig, isLoading }) => {
  const giftsDisplayed = []
  const renderItem = (item) => {
    const giftToDisplay =
      giftsWithoutThreshold?.find((gift) => {
        if (!gift.cartQualifies || gift.giftInCart || giftsDisplayed.includes(gift.id)) {
          return false
        }
        switch (gift.conditionalType) {
          case 'all':
            return true
          case 'products':
            return gift.conditionalIds.some((id) => id.split('/').pop() === item.product_id.toString())
          case 'collections':
            return item.collections?.some((collection) =>
              gift.conditionalIds.some((id) => id.split('/').pop() === collection.id.toString())
            )
          case 'tags':
            return item.tags?.some((tag) => gift.conditionalTags.includes(tag))
          default:
            return false
        }
      }) ?? null
    if (giftToDisplay) {
      giftsDisplayed.push(giftToDisplay.id)
    }
    return (
      <MiniCartLineItem
        key={item.key || item.id}
        item={{ ...item }}
        gift={giftToDisplay}
        handleSetCartConfig={handleSetCartConfig}
        isLoading={isLoading}
      />
    )
  }

  if (minicartType === 'sidecart') {
    const items = cart.items || []
    const addedItem = items[0]
    const otherItems = items.length > 1 ? items.slice(1) : []
    const otherCount = otherItems.length

    return (
      <div className="flex grow flex-col gap-y-1.5 px-margin py-1.5 lg:gap-y-2 lg:px-1.5" data-role="cart_items">
        {addedItem && (
          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-x-0.5">
              <div className="flex size-[20px] items-center justify-center rounded-full bg-system-success text-neutral-white">
                <div className="h-[8px] w-[12px] children:size-full">
                  <Icon id={'tick'} />
                </div>
              </div>
              <h3 className="h6 font-medium text-system-success">Added to bag</h3>
            </div>
            {renderItem(addedItem)}
          </div>
        )}
        {otherCount > 0 && (
          <div className="flex flex-col gap-y-1 border-t border-neutral-medium pt-1.5 lg:pt-2">
            <h3 className="h6 font-medium">Other items in your bag ({otherCount})</h3>
            {otherItems.map((item) => renderItem(item))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex grow flex-col gap-y-1.5 px-margin py-1.5 lg:px-1.5" data-role="cart_items">
      {cart.items.map((item) => renderItem(item))}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum
  const { giftsWithoutThreshold } = useGwp(cart, freeShippingMinimum)
  const { minicartType, discountText, totalText, minicartRewardsText, checkoutButtonTitle } = globalCart

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-gwp">
            <GwpCartBlock
              cart={cart}
              freeShippingMinimum={freeShippingMinimum}
              enableProgressBar={globalCart.gwp?.enableProgressBar}
              handleSetCartConfig={handleSetCartConfig}
              isLoading={isLoading}
              blockSpacing={true}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems
              minicartType={minicartType}
              cart={cart}
              giftsWithoutThreshold={giftsWithoutThreshold}
              isLoading={isLoading}
              handleSetCartConfig={handleSetCartConfig}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div
              className={`mb-1 flex flex-col px-margin pt-1 lg:px-1.5 ${
                minicartType === 'sidecart' ? 'gap-y-1' : 'gap-y-1.5'
              }`}
            >
              <DisplayCartTotal
                minicartType={minicartType}
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
                minicartRewardsText={minicartRewardsText}
              />
              <CheckoutButton
                minicartType={minicartType}
                checkoutButtonTitle={checkoutButtonTitle}
                itemCount={cart?.item_count ?? 0}
              />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
